import { useEffect, useMemo, useRef, useState } from "react";

import { getFrameLikeTitle } from "@excalidraw/excalidraw/frame";
import { isFrameLikeElement } from "@excalidraw/excalidraw/element/typeChecks";

import { Modal } from "../components/Modal";

import {
  exportFramesToPdf,
  pdfFileName,
  renderFrameCanvas,
  sortFramesForReading,
} from "./framePdf";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import type { FramePdfSource } from "./framePdf";

const THUMBNAIL_PX = 320;

export type FramePdfExportModalProps = {
  api: ExcalidrawImperativeAPI;
  canvasName: string | null;
  onClose: () => void;
};

/**
 * Lists the canvas's frames and saves the chosen ones as one A4 PDF, a frame
 * per page. The scene is read once on open: a dialog that re-rendered with
 * every remote stroke would shuffle thumbnails under the teacher's cursor.
 */
export const FramePdfExportModal = ({
  api,
  canvasName,
  onClose,
}: FramePdfExportModalProps) => {
  const [snapshot] = useState<FramePdfSource>(() => ({
    elements: api.getSceneElements(),
    appState: api.getAppState(),
    files: api.getFiles(),
  }));
  const frames = useMemo(
    () => sortFramesForReading(snapshot.elements.filter(isFrameLikeElement)),
    [snapshot],
  );
  const [selected, setSelected] = useState<ReadonlySet<string>>(
    () => new Set(frames.map((frame) => frame.id)),
  );
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const [failed, setFailed] = useState(false);
  const mounted = useRef(true);
  const allRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      for (const frame of frames) {
        if (cancelled) {
          return;
        }
        try {
          const canvas = await renderFrameCanvas(snapshot, frame, THUMBNAIL_PX);
          if (cancelled) {
            return;
          }
          const url = canvas.toDataURL("image/png");
          setThumbnails((current) => ({ ...current, [frame.id]: url }));
        } catch {
          // A failed thumbnail just stays an empty box.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [frames, snapshot]);

  const allSelected = frames.length > 0 && selected.size === frames.length;
  const someSelected = selected.size > 0 && !allSelected;
  useEffect(() => {
    if (allRef.current) {
      allRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const toggle = (frameId: string, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(frameId);
      } else {
        next.delete(frameId);
      }
      return next;
    });

  const exporting = progress !== null;
  const primaryLabel = progress
    ? `만드는 중… (${progress.done}/${progress.total})`
    : `선택한 ${selected.size}개 내보내기`;

  const run = async () => {
    const chosen = frames.filter((frame) => selected.has(frame.id));
    if (chosen.length === 0) {
      return;
    }
    setFailed(false);
    setProgress({ done: 0, total: chosen.length });
    try {
      await exportFramesToPdf({
        source: snapshot,
        frames: chosen,
        fileName: pdfFileName(canvasName),
        onProgress: (done, total) => {
          if (mounted.current) {
            setProgress({ done, total });
          }
        },
      });
      onClose();
    } catch {
      if (mounted.current) {
        setFailed(true);
        setProgress(null);
      }
    }
  };

  return (
    <Modal
      title="PDF로 내보내기"
      className="canvaschool-modal--wide canvaschool-pdf-export"
      primaryLabel={primaryLabel}
      pending={exporting}
      primaryDisabled={selected.size === 0 || frames.length === 0}
      onPrimary={() => void run()}
      // Closing mid-export would leave the file to download with no dialog.
      onCancel={exporting ? () => {} : onClose}
    >
      <p className="canvaschool-modal-lead">
        고른 프레임을 한 장에 하나씩 A4 PDF로 저장합니다.
      </p>
      {frames.length === 0 ? (
        <p className="canvaschool-hint">이 캔버스에는 프레임이 없습니다.</p>
      ) : (
        <>
          <label className="canvaschool-check canvaschool-pdf-export-all">
            <input
              ref={allRef}
              type="checkbox"
              checked={allSelected}
              disabled={exporting}
              onChange={(event) =>
                setSelected(
                  event.target.checked
                    ? new Set(frames.map((frame) => frame.id))
                    : new Set(),
                )
              }
            />
            <span>전체 선택</span>
            <span className="canvaschool-pdf-export-count">
              {selected.size}/{frames.length}
            </span>
          </label>
          <ul className="canvaschool-pdf-export-list">
            {frames.map((frame) => (
              <li key={frame.id}>
                <label
                  className="canvaschool-pdf-export-row"
                  data-selected={selected.has(frame.id)}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(frame.id)}
                    disabled={exporting}
                    onChange={(event) => toggle(frame.id, event.target.checked)}
                  />
                  <span className="canvaschool-pdf-export-thumb">
                    {thumbnails[frame.id] && (
                      <img alt="" src={thumbnails[frame.id]} />
                    )}
                  </span>
                  <span className="canvaschool-pdf-export-name">
                    {getFrameLikeTitle(frame)}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}
      <span className="canvaschool-visually-hidden" aria-live="polite">
        {progress ? primaryLabel : ""}
      </span>
      {failed && (
        <p className="canvaschool-error" role="alert">
          PDF를 만들지 못했습니다. 다시 시도해 주세요.
        </p>
      )}
    </Modal>
  );
};
